import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type Organization, type UserRow } from '../api';
import { useMe } from '../auth';
import { useOrganizations, useUsers } from '../data';
import { PageHead } from '../layout';
import { Btn, Badge, DataTable, ErrorBox, Field, Icon, Loading, Modal, Select, useForm, useToast } from '../ui/kit';
import { fmtDateTime } from '../format';

const ORG_TYPES = ['Engenharia', 'EPCista', 'Fornecedor', 'Montadora', 'Cliente', 'Consultoria'];
const GLOBAL_ROLE: Record<string, string> = { platform_admin: 'Administrador da plataforma', org_admin: 'Administrador da empresa', user: 'Usuário' };

export function OrganizationsPage() {
  const q = useOrganizations(); const [edit, setEdit] = useState<Organization | 'new' | null>(null);
  return (
    <>
      <PageHead eyebrow="Administração" title="Empresas" sub="A empresa dona e as participantes: contratadas, fornecedores e clientes.">
        <button className="btn primary" onClick={() => setEdit('new')}><Icon name="plus" />Nova empresa</button>
      </PageHead>
      <div className="panel">
        {q.isLoading ? <Loading /> : q.error ? <div className="panel-body"><ErrorBox error={q.error} /></div> : (
          <DataTable rows={q.data ?? []} onRow={setEdit} search={o => `${o.name} ${o.cnpj ?? ''} ${o.type}`} initialSort="name"
            cols={[
              { key: 'name', label: 'Empresa', sort: o => o.name, render: o => <>{o.name}{o.isOwner && <> <Badge tone="accent">dona</Badge></>}</> },
              { key: 'cnpj', label: 'CNPJ', render: o => <span className="mono">{o.cnpj}</span> },
              { key: 'type', label: 'Tipo', sort: o => o.type }
            ]} />
        )}
      </div>
      {edit && <OrgForm item={edit === 'new' ? undefined : edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function OrgForm({ item, onClose }: { item?: Organization & { contact?: string | null; email?: string | null; phone?: string | null }; onClose: () => void }) {
  const toast = useToast(); const qc = useQueryClient();
  const init = useMemo(() => ({ name: item?.name ?? '', cnpj: item?.cnpj ?? null, type: item?.type ?? 'Engenharia', contact: item?.contact ?? null, email: item?.email ?? null, phone: item?.phone ?? null }), [item]);
  const { v, set, changed } = useForm(init); const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (item) { if (Object.keys(changed).length) await api.patch(`/api/organizations/${item.id}`, { ...changed, version: item.version }); }
      else await api.post('/api/organizations', v);
      toast.ok('Empresa salva.'); await qc.invalidateQueries({ queryKey: ['organizations'] }); onClose();
    } catch (e) { toast.err(e); } finally { setBusy(false); }
  };
  return (
    <Modal title={item ? item.name : 'Nova empresa'} onClose={onClose} foot={<><button className="btn" onClick={onClose}>Cancelar</button><Btn className="btn primary" why={busy ? 'Aguarde: gravando.' : v.name.trim().length < 2 && 'Preencha a razão social.'} onClick={save}>Salvar</Btn></>}>
      <div className="form-grid">
        <Field label="Razão social" required full><input className="input" value={v.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="CNPJ"><input className="input mono" value={v.cnpj ?? ''} onChange={e => set('cnpj', e.target.value || null)} /></Field>
        <Field label="Tipo" required>{item?.isOwner ? <input className="input" value="Proprietária" disabled /> : <Select value={v.type} onChange={x => set('type', x ?? 'Engenharia')} options={ORG_TYPES.map(t => ({ value: t, label: t }))} />}</Field>
        <Field label="Contato"><input className="input" value={v.contact ?? ''} onChange={e => set('contact', e.target.value || null)} /></Field>
        <Field label="E-mail"><input className="input" type="email" value={v.email ?? ''} onChange={e => set('email', e.target.value || null)} /></Field>
        <Field label="Telefone"><input className="input" value={v.phone ?? ''} onChange={e => set('phone', e.target.value || null)} /></Field>
      </div>
    </Modal>
  );
}

export function UsersPage() {
  const me = useMe(); const q = useUsers(); const orgs = useOrganizations();
  const [edit, setEdit] = useState<UserRow | 'new' | null>(null);
  const orgName = (id: string) => orgs.data?.find(o => o.id === id)?.name ?? '';
  return (
    <>
      <PageHead eyebrow="Administração" title="Usuários" sub={me.globalRole === 'platform_admin' ? 'Pessoas de todas as empresas.' : 'Pessoas da sua empresa.'}>
        <button className="btn primary" onClick={() => setEdit('new')}><Icon name="plus" />Novo usuário</button>
      </PageHead>
      <div className="panel">
        {q.isLoading ? <Loading /> : q.error ? <div className="panel-body"><ErrorBox error={q.error} /></div> : (
          <DataTable rows={q.data ?? []} onRow={setEdit} search={u => `${u.name} ${u.email} ${orgName(u.organizationId)}`} initialSort="name"
            cols={[
              { key: 'name', label: 'Nome', sort: u => u.name },
              { key: 'email', label: 'E-mail', sort: u => u.email },
              { key: 'org', label: 'Empresa', sort: u => orgName(u.organizationId), render: u => orgName(u.organizationId) },
              { key: 'role', label: 'Acesso', render: u => <>{GLOBAL_ROLE[u.globalRole] ?? u.globalRole}{u.vmmFunction && <div className="small muted">{VMM_FUNCTION[u.vmmFunction]}</div>}</> },
              { key: 'last', label: 'Último acesso', sort: u => u.lastLoginAt ?? '', render: u => fmtDateTime(u.lastLoginAt) },
              { key: 'st', label: 'Situação', render: u => !u.active ? <Badge tone="crit">Inativo</Badge> : u.locked ? <Badge tone="warn">Bloqueado</Badge> : <Badge tone="ok">Ativo</Badge> }
            ]} />
        )}
      </div>
      {edit && <UserForm item={edit === 'new' ? undefined : edit} onClose={() => setEdit(null)} />}
    </>
  );
}

/** Funções da VMM que podem acessar o financeiro (fluxo de caixa). */
export const VMM_FUNCTION: Record<string, string> = { gerente: 'Gerente de projetos', diretor_eng: 'Diretor de engenharia', diretor_fin: 'Diretor financeiro' };

function UserForm({ item, onClose }: { item?: UserRow; onClose: () => void }) {
  const me = useMe(); const toast = useToast(); const qc = useQueryClient(); const orgs = useOrganizations();
  const init = useMemo(() => ({ name: item?.name ?? '', email: item?.email ?? '', organizationId: item?.organizationId ?? me.organizationId, globalRole: item?.globalRole ?? 'user', position: item?.position ?? null, vmmFunction: item?.vmmFunction ?? null as string | null }), [item, me.organizationId]);
  const { v, set, changed } = useForm(init); const [busy, setBusy] = useState(false); const [temp, setTemp] = useState<string | null>(null);
  const platform = me.globalRole === 'platform_admin';
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });
  const save = async () => {
    setBusy(true);
    try {
      if (item) { if (Object.keys(changed).length) await api.patch(`/api/users/${item.id}`, { ...changed, version: item.version }); toast.ok('Usuário salvo.'); await refresh(); onClose(); }
      else { const r = await api.post<{ id: string; temporaryPassword: string | null }>('/api/users', v); await refresh(); setTemp(r.temporaryPassword); }
    } catch (e) { toast.err(e); } finally { setBusy(false); }
  };
  const reset = async () => { try { const r = await api.post<{ temporaryPassword: string }>(`/api/users/${item!.id}/reset-password`); setTemp(r.temporaryPassword); await refresh(); } catch (e) { toast.err(e); } };
  const toggle = async () => { try { await api.patch(`/api/users/${item!.id}`, { active: !item!.active, version: item!.version }); toast.ok(item!.active ? 'Usuário desativado e sessões encerradas.' : 'Usuário reativado.'); await refresh(); onClose(); } catch (e) { toast.err(e); } };
  if (temp) return (
    <Modal title="Senha temporária" size="narrow" onClose={onClose} foot={<button className="btn primary" onClick={onClose}>Entendi</button>}>
      <div className="stack">
        <p>Entregue esta senha à pessoa por um canal seguro. Ela será obrigada a trocar no primeiro acesso.</p>
        <div className="callout"><span className="mono" style={{ fontSize: 18, userSelect: 'all' }}>{temp}</span></div>
        <button className="btn" onClick={() => { navigator.clipboard?.writeText(temp).then(() => toast.ok('Copiada.'), () => toast.ok('Selecione a senha e copie manualmente.')); }}>Copiar</button>
        <p className="small muted">Ela não será mostrada de novo.</p>
      </div>
    </Modal>
  );
  return (
    <Modal title={item ? item.name : 'Novo usuário'} onClose={onClose}
      foot={<>
        {item && item.id !== me.id && <><button className="btn" onClick={reset}>Gerar senha temporária</button><button className={'btn ' + (item.active ? 'danger' : '')} onClick={toggle}>{item.active ? 'Desativar' : 'Reativar'}</button></>}
        <span className="spacer" /><button className="btn" onClick={onClose}>Cancelar</button>
        <Btn className="btn primary" why={busy ? 'Aguarde: gravando.' : v.name.trim().length < 2 ? 'Preencha o nome.' : !v.email.includes('@') && 'Preencha um e-mail válido.'} onClick={save}>Salvar</Btn>
      </>}>
      <div className="form-grid">
        <Field label="Nome" required><input className="input" value={v.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="E-mail" required><input className="input" type="email" value={v.email} onChange={e => set('email', e.target.value)} /></Field>
        <Field label="Empresa" required>{platform ? <Select value={v.organizationId} onChange={x => x && set('organizationId', x)} options={(orgs.data ?? []).map(o => ({ value: o.id, label: o.name }))} /> : <input className="input" disabled value={orgs.data?.find(o => o.id === v.organizationId)?.name ?? ''} />}</Field>
        <Field label="Cargo"><input className="input" value={v.position ?? ''} onChange={e => set('position', e.target.value || null)} /></Field>
        <Field label="Acesso administrativo" help="O perfil em cada projeto é definido na equipe do projeto.">
          <Select value={v.globalRole} onChange={x => set('globalRole', x ?? 'user')} options={Object.entries(GLOBAL_ROLE).filter(([k]) => platform || k !== 'platform_admin').map(([value, label]) => ({ value, label }))} />
        </Field>
        {platform && orgs.data?.find(o => o.id === v.organizationId)?.isOwner && (
          <Field label="Função no financeiro" help="Só estas funções (e o administrador da plataforma) podem receber acesso ao fluxo de caixa.">
            <Select value={v.vmmFunction} onChange={x => set('vmmFunction', x)} empty="Nenhuma (sem acesso ao financeiro)" options={Object.entries(VMM_FUNCTION).map(([value, label]) => ({ value, label }))} />
          </Field>
        )}
        {!item && <p className="full small muted">Uma senha temporária será gerada e mostrada uma única vez.</p>}
      </div>
    </Modal>
  );
}
